// frontend/client/src/components/MinoOS.jsx
//
// Free-first Mino: real pose art + Framer Motion crossfade, driven by
// useMinoStore instead of window.triggerMino. No Rive dependency yet —
// upgrade path is documented in useMinoStore.js if usage justifies it later.

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMinoStore } from '../store/useMinoStore';

import minoIdle from '../assets/mino/mino-idle.png';
import minoThinking from '../assets/mino/mino-thinking.png';
import minoSuccess from '../assets/mino/mino-success.png';
import minoError from '../assets/mino/mino-error.png';

const POSES = {
  idle: minoIdle,
  thinking: minoThinking,
  success: minoSuccess,
  error: minoError,
};

const MINO_CSS = `
  .mino-container {
    position: fixed;
    bottom: 90px;
    right: 24px;
    z-index: 100;
    display: flex;
    align-items: flex-end;
    gap: 12px;
    pointer-events: none;
  }

  .mino-avatar-wrap {
    width: 84px;
    height: 84px;
    position: relative;
    pointer-events: auto;
  }

  .mino-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 8px 20px rgba(124, 58, 237, 0.25));
  }

  .mino-status-bubble {
    background: rgba(17, 24, 39, 0.85);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 10px 14px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    pointer-events: auto;
    max-width: 220px;
    cursor: pointer;
  }

  .mino-status-text {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    color: white;
    font-family: var(--font-body);
    line-height: 1.4;
  }

  .mino-status-subtext {
    margin: 4px 0 0 0;
    font-size: 10px;
    color: #9CA3AF;
    line-height: 1.4;
  }
`;

export default function MinoOS() {
  const { status, message, subMessage, dismiss } = useMinoStore();
  const isBouncing = status === 'thinking';

  return (
    <div className="mino-container">
      <style>{MINO_CSS}</style>

      <AnimatePresence>
        {status !== 'idle' && message && (
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 10, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            className="mino-status-bubble"
            onClick={dismiss}
            title="Dismiss"
          >
            <p className="mino-status-text">{message}</p>
            {subMessage && <p className="mino-status-subtext">{subMessage}</p>}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mino-avatar-wrap">
        <AnimatePresence mode="wait">
          <motion.img
            key={status}
            src={POSES[status] || POSES.idle}
            alt="Mino"
            className="mino-avatar-img"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: isBouncing ? [0, -6, 0] : 0,
            }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{
              opacity: { duration: 0.25 },
              scale: { duration: 0.25 },
              y: isBouncing
                ? { repeat: Infinity, duration: 1.4, ease: 'easeInOut' }
                : { duration: 0.25 },
            }}
          />
        </AnimatePresence>
      </div>
    </div>
  );
}